import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ShieldCheck, Smile, Clock, Home as HomeIcon, Key, MapPin } from 'lucide-react';
import PropertyCard from '../components/PropertyCard';
import { TransactionType } from '../types';
import { supabase } from '../lib/supabase';

const HERO_IMAGES = [
  "https://res.cloudinary.com/dgsmu4bgt/image/upload/v1764428874/red-star-square-cotonou_twv2qe.jpg",
  "https://res.cloudinary.com/dgsmu4bgt/image/upload/v1764428874/unnamed_6_uxxcwt.webp",
  "https://res.cloudinary.com/dgsmu4bgt/image/upload/v1764428874/unnamed_opmgso.webp",
  "https://res.cloudinary.com/dgsmu4bgt/image/upload/v1764429065/590470112276001734252650_llykp7.jpg",
  "https://res.cloudinary.com/dgsmu4bgt/image/upload/v1764429065/306810711444001734252735_cmzwmx.jpg",
  "https://res.cloudinary.com/dgsmu4bgt/image/upload/v1764428875/506348523_10071271409655472_976162581417299712_n_hfw5wo.jpg",
  "https://res.cloudinary.com/dgsmu4bgt/image/upload/v1764428878/IMG-20250922-WA0031_r51lky.jpg"
];

const ZONES = ["Cotonou", "Porto-Novo", "Ouidah", "Abomey", "Parakou"];

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState<TransactionType>('Location');
  const [searchZone, setSearchZone] = useState<string>('');
  const [heroImageIndex, setHeroImageIndex] = useState(0);
  const [biens, setBiens] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setHeroImageIndex((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    fetchBiens();
  }, []);

  const fetchBiens = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('biens_publics')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) throw error;
      setBiens(data || []);
    } catch (err) {
      console.error('Supabase error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/nos-biens', { state: { transactionType: searchType, quartier: searchZone } });
  };

  return (
    <div className="flex flex-col min-h-screen">
      {/* Floating Background Icons */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden hidden md:block opacity-50">
        <HomeIcon className="absolute top-1/4 left-10 text-orange/20 animate-bounce duration-[3000ms]" size={40} />
        <Key className="absolute bottom-1/3 right-10 text-navy/20 animate-pulse duration-[2000ms]" size={30} />
        <MapPin className="absolute top-1/3 right-1/4 text-orange/10 animate-bounce duration-[4000ms]" size={50} />
      </div>

      {/* Hero Section */}
      <section className="relative h-[80vh] min-h-[600px] flex items-center justify-center overflow-hidden">
        {HERO_IMAGES.map((img, index) => (
            <div 
                key={index}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === heroImageIndex ? 'opacity-100' : 'opacity-0'}`}
            >
                <div 
                    className={`absolute inset-0 bg-cover bg-center transition-transform duration-[6000ms] ease-linear ${index === heroImageIndex ? 'scale-110' : 'scale-100'}`}
                    style={{ backgroundImage: `url(${img})` }}
                />
            </div>
        ))}
        <div className="absolute inset-0 bg-black/40" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight animate-fade-in-up">
              Trouvez ce qui vous <br/>
              <span className="text-orange">correspond au Bénin</span>
            </h1>
            <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
              Une expérience immobilière transparente et fluide pour louer, acheter ou vendre en toute confiance.
            </p>
          </div>

          <div className="bg-white/95 backdrop-blur-sm p-3 rounded-2xl shadow-2xl max-w-4xl mx-auto border border-white/20">
            <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-2">
              <div className="flex-1 bg-gray-50 rounded-xl px-4 py-2 border-r-0 md:border-r border-gray-200">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Je cherche</label>
                <select 
                  className="w-full bg-transparent font-semibold text-navy focus:outline-none cursor-pointer"
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value as TransactionType)}
                >
                  <option value="Location">Une Location</option>
                  <option value="Vente">Un Achat</option>
                  <option value="Meublé">Un Meublé</option>
                </select>
              </div>
              
              <div className="flex-[2] bg-gray-50 rounded-xl px-4 py-2">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Zone de recherche</label>
                <select 
                  className="w-full bg-transparent font-semibold text-navy focus:outline-none cursor-pointer"
                  value={searchZone}
                  onChange={(e) => setSearchZone(e.target.value)}
                >
                  <option value="">Toutes les zones</option>
                  {ZONES.sort().map(q => (
                    <option key={q} value={q}>{q}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <button type="submit" className="w-full md:w-auto bg-orange hover:bg-orange/90 text-white font-bold py-4 px-8 rounded-xl transition shadow-lg shadow-orange/30 whitespace-nowrap">
                  Rechercher
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Recent Properties */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-navy mb-2">Dernières annonces</h2>
              <p className="text-gray-600 font-medium">Les nouveautés du marché immobilier béninois</p>
            </div>
            <Link to="/nos-biens" className="hidden md:inline-block text-navy font-bold hover:text-orange transition-colors">
              Voir tout →
            </Link>
          </div>

          {loading ? (
            <div className="flex justify-center py-20 text-navy font-bold">Chargement des biens...</div>
          ) : biens.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {biens.map(property => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-gray-500 font-medium">Aucun bien disponible pour le moment.</div>
          )}
        </div>
      </section>

      {/* Why LDF Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy mb-4">Pourquoi choisir LDF ?</h2>
            <div className="w-24 h-1.5 bg-orange mx-auto rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="text-center p-8 rounded-3xl border border-gray-100 hover:shadow-xl transition duration-300 group">
              <div className="bg-blue-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-navy group-hover:scale-110 transition duration-300">
                <ShieldCheck size={40} />
              </div>
              <h3 className="text-xl font-bold mb-4 text-navy">Biens vérifiés</h3>
              <p className="text-gray-600">Contrôles rigoureux pour garantir la qualité et éviter les arnaques.</p>
            </div>
            <div className="text-center p-8 rounded-3xl border border-gray-100 hover:shadow-xl transition duration-300 group">
              <div className="bg-orange/10 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-orange group-hover:scale-110 transition duration-300">
                <Smile size={40} />
              </div>
              <h3 className="text-xl font-bold mb-4 text-navy">Accompagnement</h3>
              <p className="text-gray-600">Nos agents vous guident tout au long de votre démarche.</p>
            </div>
            <div className="text-center p-8 rounded-3xl border border-gray-100 hover:shadow-xl transition duration-300 group">
              <div className="bg-green-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-green-600 group-hover:scale-110 transition duration-300">
                <Clock size={40} />
              </div>
              <h3 className="text-xl font-bold mb-4 text-navy">Rapide & Transparent</h3>
              <p className="text-gray-600">Un processus fluide pour une expérience sans friction.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
